const config = {};
for (const configElement of document.getElementsByClassName('js-configuration')) {
  try {
    Object.assign(config, JSON.parse(configElement.text));
  } catch (error) {
    console.error(error);
  }
}

export const debug = window.location.hash.indexOf('debug') !== -1;
export const {
  isSocialConnect,
  pathname,
  secureURL,
  isUserIdentified, // it's allowed to read informations about the user (his/her name is displayed in the header)
  isUserAuthenticated, // it's allowed to read and write user informations (place an order, cancel order, add to wishlist, etc.)
  cookiesHostname,
  cookiesPath,
  // See also isTouch
  isMobile,
  isMobileApplication,
  searchIncludeFnacSpectacle,
  fnacSpectacleURL,
  searchFnacSpectacleMaxShows,
  isFnacPro,
  locale,
  lang,
  region,
  currency,
  currencyHTML,
  cartViewURL,
  cartURL,
  cartAddURL,
  cartDeleteURL,
  cartUpdateURL,
  cartOneClickURL,
  loginURL,
  reauthURL,
  createAccountURL,
  apiAuth,
  searchAutoCompleteURL,
  searchAutoCompleteGroups,
  searchAutoCompleteURL2,
  symbolsURL, // used by custom-mustache-loader
  productSendReviewsFeedbackURL,
  favoriteStoreURL,
  setNearStoreURL,
  saveFavoriteStoreURL,
} = config;

export const headerTabTemplateURL = templateURL(config.headerTabTemplateURL);

// Template URLs (parameterizable URLs):
export const fnacSpectacleSearchTemplateURL = templateURL(config.fnacSpectacleSearchTemplateURL);
export const excludeFromAdvicesTemplateURL = templateURL(config.excludeFromAdvicesTemplateURL);
export const putValuationTemplateURL = templateURL(config.putValuationTemplateURL);
export const putRelationTemplateURL = templateURL(config.putRelationTemplateURL);
export const putInterestRelationTemplateURL = templateURL(config.putInterestRelationTemplateURL);
export const deleteInterestRelationTemplateURL = templateURL(config.deleteInterestRelationTemplateURL);
export const geocodingCacheTemplateURL = templateURL(config.geocodingTemplateURL);

// Compare
export const compareTemplateURL = templateURL(config.compareTemplateURL);

// Wishlist
export const { getWishlistsURL, createWishlistURL } = config;
export const addWishlistItemTemplateURL = templateURL(config.addWishlistItemTemplateURL);
export const deleteWishlistItemTemplateURL = templateURL(config.deleteWishlistItemTemplateURL);
export const myAccountWishlistTemplateURL = templateURL(config.myAccountWishlistTemplateURL);

// Retrait Magasin
export const getNearestPickUpStoresURL = templateURL(config.getNearestPickUpStoresURL);

// Popin dle
export const getShippingOptionURL = templateURL(config.getShippingOptionURL);
export const getListStoreURL = templateURL(config.getListStoreURL);

// if the mediaquery is supported (media="not all" if not supported)
// See https://github.com/Modernizr/Modernizr/blob/master/feature-detects/touchevents.js
// true = at least one media query match (others are not supported or doesn't match)
// false = at least one media query is supported, but none of supported media query match
// null = none of media queries are supported
const isTouchMediaMatches = ['(pointer: coarse)', '(hover: none)', '(-moz-touch-enabled)'].reduce((result, query) => {
  const { media, matches } = matchMedia(query);

  // This media query is not supported
  if (media === 'not all') {
    return result;
  }

  return result || matches;
}, null);
// fallback to API detection for browsers that dont support used mediaqueries
const isTouchWithFallback =
  isTouchMediaMatches !== null
    ? isTouchMediaMatches
    : 'ontouchstart' in window || window.TouchEvent || (window.DocumentTouch && document instanceof DocumentTouch);

// is primary pointer have limited accuracy or the primary input mechanism can hover
// see also isMobile
export const isTouch = isTouchWithFallback;

/**
 * Create a route template function
 * Try to support the same routes syntax that ASP.Net use
 * @param {string} url
 * @param {object} [defaultData]
 * @return {function(*=): string}
 *
 * @see https://docs.microsoft.com/en-us/aspnet/core/fundamentals/routing?view=aspnetcore-2.2 ASP.Net routing
 * @see https://github.com/aspnet/AspNetCore/blob/master/src/Http/Routing/src/Patterns/RoutePatternFactory.cs
 * @see https://github.com/aspnet/AspNetCore/blob/master/src/Http/Routing/src/InlineRouteParameterParser.cs
 * @see https://github.com/aspnet/AspNetCore/blob/master/src/Http/Routing/src/Patterns/RoutePatternParser.cs
 * @see https://github.com/aspnet/AspNetCore/blob/master/src/Http/Routing/src/Patterns/RouteParameterParser.cs
 * @see https://github.com/aspnet/AspNetCore/blob/master/src/Mvc/Mvc.Core/src/Routing/UrlHelper.cs Action, RouteUrl and GetVirtualPathData
 * @example
 * const myTemplateURL = templateURL("{controller=Home}/{action=Index}/{id:int?}");
 * myTemplateURL({id: 10})//> "Home/Index/10"
 * myTemplateURL()//> "Home/Index"
 */
export function templateURL(url, defaultData = {}) {
  const [
    ,
    // ignored
    base = '/',
    route = '',
  ] = /^((?:(?:https?:)?\/\/[^/]+)?\/)(.*)$/.exec(url || '') || [];

  // - "=value": will use "value" as default
  // - ":transformer": will use "transformer" to match
  // - "?": default value is UrlParameter.Optional
  const regexp = /^{(\*\*?)?([^{].*?)(:.*?)?(?:=(.*?)|(\?))?}$/;

  const routeSegments = route.split('/').map((segment) => {
    if (segment === '') {
      throw new Error('Invalid route segment');
    }

    // Parse all parameter parts
    return segment
      .split(/({[^{].*?})/)
      .filter((part) => part !== '')
      .map((part) => {
        const match = regexp.exec(part);
        if (match) {
          const [
            ,
            // ignored
            escape = '',
            name,
            contraints = null,
            defaultValue = null,
          ] = match;
          const isOptional = match[5] === '?';

          if (isOptional && defaultValue !== null) {
            throw new Error(`Optional param ${name} can't have a default value`);
          }

          return {
            name,
            isOptional,
            defaultValue,
          };
        } else {
          // It's a literal
          return part;
        }
      });
  });

  return (data) => {
    data = Object.assign({}, defaultData, data);

    const finalRouteSegments = routeSegments
      .map((parts) => {
        let result = '';
        for (const part of parts) {
          let value = part;

          if (part instanceof Object) {
            const { name, isOptional, defaultValue } = part;
            const hasValue = Object.prototype.hasOwnProperty.call(data, name);

            if (!hasValue && isOptional) {
              return null; // this segment contains an optional param without any value, we ignore it
            }

            if (!hasValue && defaultValue === null) {
              throw new Error(`No value is provided for param ${name}`);
            }

            value = hasValue ? encodeURIComponent(data[name]) : defaultValue;
          }

          result += value;
        }

        return result;
      })
      .filter((segment) => segment);

    return base + finalRouteSegments.join('/');
  };
}
